<template>
  <div :class="$style.gridFooter">
    <div :class="$style.footerCell">
      <InputText
        v-model="reward.name"
        placeholder="Enter name"
        :class="`${$style.nameInput} ${errors.name ? 'p-invalid' : ''}`"
      />
      <div v-if="errors.name" :class="$style.error">{{ errors.name }}</div>
    </div>
    <div :class="$style.footerCell">
      <SpriteSelector
        :model-value="reward.sprite"
        :options="sprites"
        @select="handleSelectSprite"
        @upload="handleUploadSprite"
        @delete="handleDeleteSprite"
        @link="handleLinkSprite"
        @unlink-sprite="handleUnlinkSprite"
      />
    </div>
    <div :class="$style.footerCell">
      <div ref="toggleRef" @click="toggleOverlayPanel">
        <InputText v-model="rewardItemsText" placeholder="Select items" readonly :class="$style.dropdownTrigger" />
      </div>
      <Popover ref="overlayPanel">
        <ItemsBundlesList :reward-items="reward.items" :show-confirmation="false" @save="handleItemsSave" />
      </Popover>
      <div v-if="errors.items" :class="$style.error">{{ errors.items }}</div>
    </div>
    <div :class="$style.footerCell">
      <Button icon="pi pi-times" severity="secondary" outlined @click="resetForm" />
      <Button icon="pi pi-check" @click="addReward" />
    </div>
  </div>
</template>

<script setup lang="ts">
import SpriteSelector from "@/components/SpriteSelector";
import useSprites from "@/modules/Items/useSprites";
import ItemsBundlesList from "@/modules/ItemsBundles";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import useRewards from "../hooks/useRewards";

const emit = defineEmits(["add-reward", "reset-form"]);

const route = useRoute();
const gameId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);

const { rewardsToDisplay } = useRewards(gameId.value, versionId.value);

interface Reward {
  sprite: { id: string; name: string; imageUrl: string } | undefined;
  name: string;
  items: any[];
}

const reward = reactive<Reward>({
  sprite: undefined,
  name: "",
  items: [],
});

interface Errors {
  name: string | null;
  items: string | null;
  all: string | null;
}

const errors = reactive<Errors>({
  name: null,
  items: null,
  all: null,
});

const spriteType = "REWARD";

const { sprites: rawSprites, uploadSprite, deleteSprite } = useSprites(gameId.value, versionId.value, spriteType);

const sprites = computed(() =>
  rawSprites.value.filter((sprite): sprite is { id: string; name: string; imageUrl: string } => !!sprite)
);

const rewardItemsText = computed(
  () =>
    reward.items
      ?.map((item) => item?.selectedItem?.name ?? "")
      .filter((name) => name)
      .join(", ") ?? ""
);

const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);

const handleItemsSave = (items: any[]) => {
  reward.items = items;
  overlayPanel.value?.hide();
};

const handleSelectSprite = (sprite: { id: string; name: string; imageUrl: string } | undefined) => {
  reward.sprite = sprite;
};

const handleUploadSprite = async (event: { files: File[] }) => {
  await uploadSprite(event.files, spriteType);
};

const handleDeleteSprite = (id: string) => {
  deleteSprite(id);
};

const handleLinkSprite = (rewardId: string, spriteId: string) => {
  reward.sprite = sprites.value.find((sprite: { id: string }) => sprite.id === spriteId);
};

const handleUnlinkSprite = () => {
  reward.sprite = undefined;
};

const resetForm = () => {
  reward.name = "";
  reward.sprite = undefined;
  reward.items = [];

  errors.name = null;
  errors.items = null;
  errors.all = null;

  emit("reset-form");
};

const addReward = () => {
  let isValid = true;
  const namePattern = /^[a-zA-Z][a-zA-Z0-9 _]*[a-zA-Z0-9]$/;
  if (!reward.name) {
    errors.name = "Name is required";
    isValid = false;
  } else if (!namePattern.test(reward.name)) {
    errors.name = "Name must be alphanumeric and can include spaces, but start with a letter";
    isValid = false;
  } else if (rewardsToDisplay.value.some((r: Reward) => r.name.toLowerCase() === reward.name.toLowerCase())) {
    errors.name = "Name already exists";
    isValid = false;
  } else {
    errors.name = null;
  }

  if (reward.items.length === 0) {
    errors.items = "At least one item is required";
    isValid = false;
  } else {
    errors.items = null;
  }

  if (!isValid) {
    return;
  }

  try {
    const transformedReward = {
      ...reward,
      sprite: reward.sprite?.id ?? undefined,
      items: reward.items.map((item) => ({
        id: item.selectedItem.id,
        quantity: item.quantity,
      })),
    };
    emit("add-reward", transformedReward);
    resetForm();
  } catch (error) {
    console.error("Error adding reward:", error);
    errors.all = "Failed to add reward. Please try again.";
  }
};

const toggleOverlayPanel = (event: MouseEvent) => {
  overlayPanel.value?.toggle(event);
};
</script>

<style module>
.gridFooter {
  display: grid;
  grid-template-columns: 18% 32% 30% 20%;
  width: 100%;
  box-sizing: border-box;
}

.footerCell:last-child > * {
  margin-left: 10px;
}

.footerCell:not(:first-child) {
  padding-left: 1rem;
  padding-right: 1rem;
}

.footerCell:first-child {
  padding-right: 1rem;
}

.error {
  color: red;
  font-size: 12px;
  font-weight: 400;
}

.dropdownTrigger {
  width: 100%;
  cursor: pointer;
}

.nameInput {
  width: 100%;
}
</style>
